import { Role } from "@/enums/Role";
import StorageWrapper from "@/helpers/storage";
import timezones from "./timezones";
import {
	ScheduleCallPlaceholders,
	LiveCallPlaceholders,
	RecordingRequestPlaceholders,
	GoogleReviewPlaceholders
} from "../enums/Placeholders";
import { Colors } from "@/types/tags/Colors";
import { countCodePoints, countUtf16 } from "@common-ui/utilities/characterCount";
import { expandPlaceholdersForCounting, templateOverflowMessage } from "@/helpers/templatePlaceholderCount";
import { genericFailure, supportEmail } from "@/helpers/userMessages";
import { knownUploadErrorMessage } from "@/helpers/uploadErrorMessages";

const segmenter = new Intl.Segmenter("en", { granularity: "grapheme" });

export function getColor(color) {
	return color ?? Colors[0];
}

const AVATAR_BACKGROUND_OPACITY = 0.3; // matches .team-avatar .v-avatar__underlay --v-activated-opacity
const PAGE_BACKGROUND_CHANNEL = 245; // #f5f5f5 theme background
const MINIMUM_CONTRAST_RATIO = 4.5; // WCAG AA for normal text

function parseHexColor(hex) {
	if (typeof hex !== "string") {
		return null;
	}
	const normalized = hex.trim().replace("#", "");
	const expanded = normalized.length === 3 ? normalized.replace(/(.)/g, "$1$1") : normalized;
	if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
		return null;
	}
	return [parseInt(expanded.slice(0, 2), 16), parseInt(expanded.slice(2, 4), 16), parseInt(expanded.slice(4, 6), 16)];
}

function channelToLinear(value) {
	const channel = value / 255;
	return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

function getRelativeLuminance([red, green, blue]) {
	return 0.2126 * channelToLinear(red) + 0.7152 * channelToLinear(green) + 0.0722 * channelToLinear(blue);
}

function getContrastRatio(luminanceA, luminanceB) {
	const lighter = Math.max(luminanceA, luminanceB);
	const darker = Math.min(luminanceA, luminanceB);
	return (lighter + 0.05) / (darker + 0.05);
}

function toHexColor([red, green, blue]) {
	return "#" + [red, green, blue].map((value) => Math.round(value).toString(16).padStart(2, "0")).join("");
}

/**
 * Returns a hue-matched text color for avatar initials that meets WCAG AA contrast
 * against the avatar's tinted background (the palette color composited at 30% opacity
 * over the #f5f5f5 page background). The tinted background is always light, so the
 * color is progressively darkened (preserving its hue) until it clears the threshold.
 * A sufficiently dark candidate always passes against a light background, so the trailing
 * "#000000" return is an unreachable safety net for the current palette.
 */
export function getReadableInitialsColor(color) {
	const rgb = parseHexColor(getColor(color));
	if (!rgb) {
		return "currentColor";
	}

	const renderedBackground = rgb.map(
		(channel) => channel * AVATAR_BACKGROUND_OPACITY + PAGE_BACKGROUND_CHANNEL * (1 - AVATAR_BACKGROUND_OPACITY)
	);
	const backgroundLuminance = getRelativeLuminance(renderedBackground);

	for (let factor = 1; factor >= 0; factor -= 0.05) {
		const candidate = rgb.map((channel) => channel * factor);
		if (getContrastRatio(backgroundLuminance, getRelativeLuminance(candidate)) >= MINIMUM_CONTRAST_RATIO) {
			return toHexColor(candidate);
		}
	}

	return "#000000";
}

/**
 * Returns "#ffffff" or "#000000" — whichever reads better as text/foreground on a
 * SOLID fill of the given hex color. Used for opaque (flat) label chips whose fill
 * is a user-chosen palette color (see Colors.ts) that can be anything from near-white
 * (#FFD41B, #E6FFE3) to near-black (#03011D). Picking the higher-contrast of black/white
 * keeps the label legible across the whole palette, which getReadableInitialsColor
 * (tuned for the 30%-opacity tonal avatar tint) does not solve for a solid fill.
 */
export function getReadableTextColor(color) {
	const rgb = parseHexColor(getColor(color));
	if (!rgb) {
		return "#000000";
	}
	const backgroundLuminance = getRelativeLuminance(rgb);
	const contrastWithWhite = getContrastRatio(backgroundLuminance, getRelativeLuminance([255, 255, 255]));
	const contrastWithBlack = getContrastRatio(backgroundLuminance, getRelativeLuminance([0, 0, 0]));
	return contrastWithWhite >= contrastWithBlack ? "#ffffff" : "#000000";
}

Number.prototype.isBetween = function (a, b) {
	const min = Math.min(a, b);
	const max = Math.max(a, b);
	return this >= min && this <= max;
};

export function safeGetItem(key) {
	try {
		return StorageWrapper.getItem(key);
	} catch (e) {
		console.error("Error accessing storage wrapper:", e);
		return null;
	}
}

export function safeSetItem(key, value) {
	try {
		StorageWrapper.setItem(key, value);
		return true;
	} catch (e) {
		console.error("Error setting storage wrapper item:", e);
		return false;
	}
}

export function setDefaultPhoneCountry(countryCode) {
	if (!countryCode) {
		return;
	}
	const normalized = String(countryCode).toUpperCase();

	if (window.VueTelProps && window.VueTelProps.preferredCountries) {
		const preferred = window.VueTelProps.preferredCountries;
		const exists = preferred.some((c) => c.localeCompare(normalized, undefined, { sensitivity: "base" }) === 0);

		if (!exists) {
			window.VueTelProps.preferredCountries = [normalized, ...preferred];
		}
	}
}

export function beautifyName(name) {
	if (!name) {
		return "";
	}
	return name
		.split(" ")
		.filter((n) => n.length > 0)
		.map((n) => n[0].toUpperCase() + n.slice(1))
		.join(" ");
}

export function getCustomerDisplayName(record) {
	if (!record) {
		return "";
	}
	if (record.name && record.name.trim()) {
		return record.name;
	} else if (record.PhoneNumbers?.length > 0) {
		return record.PhoneNumbers[0].phoneNumber;
	} else if (record.EmailAddresses?.length > 0) {
		return record.EmailAddresses[0].emailAddress;
	}
	return "";
}

export function getCustomerViewDisplayName(record) {
	if (!record) {
		return "";
	}

	const nameParts = [];

	if (record.firstname) {
		nameParts.push(record.firstname);
	}
	if (record.lastname) {
		nameParts.push(record.lastname);
	}

	if (nameParts.length > 0) {
		return nameParts.join(" ");
	} else if (record.emails && record.emails.length > 0) {
		return record.emails;
	} else if (record.phones && record.phones.length > 0) {
		return record.phones;
	}

	return "";
}

export function getUserDisplayName(record) {
	if (!record) {
		return "";
	}
	if (record.name && record.name.trim()) {
		return record.name;
	} else if (record.firstname && record.lastname) {
		return record.firstname + " " + record.lastname;
	} else if (record.phone) {
		return record.phone;
	} else if (record.email) {
		return record.email;
	} else if (record.PhoneNumbers && record.PhoneNumbers.length > 0) {
		return record.PhoneNumbers[0].phoneNumber;
	} else if (record.EmailAddresses && record.EmailAddresses.length > 0) {
		return record.EmailAddresses[0].emailAddress;
	}
	return "";
}

export function setStartedByColumn(item, userId) {
	const x = item.User;
	if (!x) {
		return "";
	}
	let resp = x.firstname ? x.firstname + " " + x.lastname : x.email;
	if (x.id == userId) {
		resp += " (You)";
	}
	return resp;
}

// Report creators come from Scribe analysis metadata (name/email), not a contact-admin User/Account
// row, so unlike agentAvatarColor (ProjectsPage.vue/ConversationsPage.vue's "Started by"/"Team"
// columns) there is no stored per-user color to key off of. Hashes the name into the same Colors
// palette instead, so a given creator's avatar is a stable color across rows/reloads without a
// backend change (LSP-20294).
export function getColorForName(name) {
	if (!name) return getColor(undefined);
	let hash = 0;
	for (let index = 0; index < name.length; index++) {
		hash = (hash * 31 + name.charCodeAt(index)) | 0;
	}
	return Colors[Math.abs(hash) % Colors.length];
}

export function getAgentNameInitials(agentName) {
	if (!agentName) return "-";
	const names = agentName.split(" ");
	if (names.length === 1) {
		return names[0].split("@")[0].substring(0, 2).toUpperCase();
	}
	return names
		.filter((x) => x.length > 0)
		.map((x) => x[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

export function getDisplayDate(comment) {
	return new Date(comment.createdAt).toLocaleDateString();
}

export function isLiveSwitchEmail(email) {
	if (!email || typeof email !== "string") {
		return false;
	}
	return email.endsWith("@liveswitch.com");
}

export function sourceWithTimestamp(source, timestamp = 5) {
	return `${source}#t=${timestamp}`;
}

export function addEmailToCustomer(customer, emailAddress) {
	// If the customer's list of email addresses is empty, add the new email address.
	if (customer.EmailAddresses.length === 0) {
		customer.EmailAddresses.push({ emailAddress: emailAddress });
	}
	// If the email address is empty, remove it from the list.
	else if (emailAddress.length === 0) {
		customer.EmailAddresses.splice(0, 1);
	}
	// Otherwise, update the existing email address.
	else {
		customer.EmailAddresses[0] = customer.EmailAddresses[0] || {};
		customer.EmailAddresses[0].emailAddress = emailAddress;
	}

	return customer;
}

export function addPhoneNumberToCustomer(customer, phoneNumber) {
	if (!phoneNumber) {
		return customer;
	}

	// If the customer's list of phone numbers is empty, add the new phone number.
	if (customer.PhoneNumbers.length === 0) {
		customer.PhoneNumbers.push({ phoneNumber });
	}
	// If the phone number is empty, remove it from the list.
	else if (phoneNumber.length === 0) {
		customer.PhoneNumbers.splice(0, 1);
	}
	// Otherwise, update the existing phone number.
	else {
		customer.PhoneNumbers[0] = customer.PhoneNumbers[0] || {};
		customer.PhoneNumbers[0].phoneNumber = phoneNumber;
	}

	return customer;
}

export function calculateTimeout(message) {
	if (!message) {
		console.warn("Message is empty, defaulting to 3200ms");
		return 3200;
	}
	return Math.min(Math.max(message.length * 50, 3200), 7000);
}

/**
 * vue-toastification `filterBeforeCreate` (LSP-20848): drop a toast whose type and text are already on
 * screen, so clicking Save or Copy again does not stack identical banners. Updates to an existing toast
 * (same id) always pass.
 */
export function dropDuplicateToast(toast, toasts) {
	const isDuplicate =
		typeof toast.content === "string" &&
		toasts.some((shown) => shown.id !== toast.id && shown.type === toast.type && shown.content === toast.content);
	return isDuplicate ? false : toast;
}

/**
 * The support address to put in customer-facing copy. Mirrors the inline
 * `window.env.VITE_SUPPORT_EMAIL || "help@liveswitch.com"` idiom already used by AppStorePage,
 * FreeTrialEndedDialog and TrialTopBar — read at CALL time, never at module scope, because
 * `window.env` is only populated after main.ts's start() fetches /config/env.json.
 */
export function uploadSupportEmail() {
	return supportEmail();
}

export function blurActiveElement() {
	if (document.activeElement) {
		try {
			document.activeElement.blur();
			document.body.focus();
		} catch {
			// Best effort to blur from the active element.
		}
	}
}

export function debounce(func, wait) {
	let timeout;
	function debounced(...args) {
		clearTimeout(timeout);
		timeout = setTimeout(() => func.apply(this, args), wait);
	}
	// Callers must be able to drop a pending call on teardown: a timer that survives its
	// owning component fires against a torn-down page and reaches api -> buildUrl -> window.
	debounced.cancel = function () {
		clearTimeout(timeout);
		timeout = undefined;
	};
	return debounced;
}

export function getSortedArrayCloneByStringKey(array, key) {
	if (!array || !key) return [];
	const arrClone = array.slice(0);
	return arrClone.sort((a, b) => a[key].localeCompare(b[key]));
}

// LSP-19484: text that betrays server internals rather than telling the customer anything.
// A message matching any of these is replaced with the caller's plain-language fallback, even
// on a 4xx, because our typed 4xx errors never phrase themselves this way.
const INTERNAL_MESSAGE_PATTERNS = [
	/Sequelize\w*Error/i,
	/\brelation "[^"]*" does not exist/i,
	/\b(?:null value in column|violates (?:not-null|unique|foreign key|check) constraint)/i,
	/^\s*(?:Error|TypeError|RangeError|ReferenceError|SyntaxError|EvalError|URIError|AggregateError)\s*:/i,
	/\bat [\w$.<>[\]]+ \(/, // a stack frame
	/\b(?:ECONNREFUSED|ECONNRESET|ETIMEDOUT|ENOTFOUND|EPIPE|EHOSTUNREACH|EAI_AGAIN)\b/,
	// A raw SQL statement, anchored so ordinary copy ("update your settings where needed") is not
	// mistaken for one.
	/(?:^|:\s*)(?:select|insert into|update|delete from)\b[\s\S]*\b(?:from|into|set|where|values)\b/i,
	/\bCannot read propert(?:y|ies)\b/i,
	/\bis not a function\b/i,
	/\bnode_modules\b|(?:^|\s)\/(?:usr|home|var|app|opt)\/[\w./-]+/,
	/Request failed with status code \d+/i,
	// LSP-21412 (reported as LSP-21401): a deliberate 4xx written for a developer rather than the
	// customer — "Contact association ID must be a valid UUID." These name identifiers, schema
	// types, or raw JavaScript values, none of which the customer can act on.
	/\bUUIDs?\b/i,
	/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i,
	/\bIDs?\b/,
	/\b[a-z]+(?:[A-Z][a-z]+)*Ids?\b/, // camelCase identifiers: projectId, organizationIds
	/\b(?:body|querystring|params|headers)\/\w+/, // a Fastify/AJV schema path
	/\bmust (?:have required property|match (?:format|pattern)|NOT have additional properties)\b/i,
	/\bmust be (?:an? )?(?:integer|number|string|boolean|array|object)\b/i,
	/\b(?:null|undefined|NaN)\b/
];

// LSP-21412: email addresses are the customer's own value, echoed back in deliberate copy ("A contact with
// the email null@example.com already exists."). Screen the sentence around them, not the address.
const EMAIL_ADDRESS = /[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+/g;

// LSP-22160: the API's duplicate-name copy quotes the customer's own name back ('An instruction template named
// "Photo ID Check" already exists.'), and a name containing a screened word ("ID", "null") hid the message behind
// the generic error. Only the quoted value inside this exact, whole-message sentence shape is exempt; quoted
// text anywhere else is still screened. Shapes come from contact-api's instructions, liveCallForms, checklists,
// attributes, attributeGroups, and admin (industry code) controllers.
const DUPLICATE_NAME_SENTENCE = /^(An? [A-Za-z ]+? (?:named|with (?:the )?(?:name|code))) "[^\n]*"( already exists(?: in this group)?\.)$/;

function looksLikeInternalServerText(message) {
	if (typeof message !== "string" || !message.trim()) return true;
	const sentence = message.replace(EMAIL_ADDRESS, "").replace(DUPLICATE_NAME_SENTENCE, '$1 ""$2');
	if (INTERNAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(sentence))) return true;
	// A multi-line wall of text is a dump, not a sentence written for a person.
	return message.includes("\n") && message.length > 200;
}

/**
 * Turns a rejected value into something safe to show a customer.
 *
 * LSP-19484: this helper used to screen ONLY 403 and hand back `response.data.message` verbatim
 * for every other status, so a server-side failure printed the server's own words into a red
 * toast (reported as `SequelizeDatabaseError: relation "projects" does not exist` on Create
 * Project). A server `message` now reaches the customer only when
 *   * the response explicitly marks it customer-safe (`userSafe`/`customerSafe`), or
 *   * it is a 4xx — where our typed errors carry deliberate, user-facing copy — AND it does not
 *     read like internal machinery.
 * Anything else becomes `fallbackMessage`. The raw text is still logged for Datadog/console.
 *
 * @param error the rejected value (axios error, typed API error, Error, or string)
 * @param fallbackMessage plain-language copy for this specific action, e.g.
 *   `couldNot("create the project")`. Callers should pass one so the customer is told which
 *   action failed instead of a generic sentence.
 */
export function getMessageFromError(error, fallbackMessage) {
	const safeFallback = typeof fallbackMessage === "string" && fallbackMessage.trim() ? fallbackMessage : genericFailure();

	if (!error) return "Unknown error occurred.";

	// If error is a string, return it directly — that is the caller's own copy, not server text.
	if (typeof error === "string") return error;

	// Prefer the structured server message on response.data over axios's generic
	// `error.message` (e.g. "Request failed with status code 403").
	const responseData = error?.response?.data;
	const uploadMessage = knownUploadErrorMessage(responseData) ?? knownUploadErrorMessage(error);
	if (uploadMessage) return uploadMessage;
	const apiMessage = responseData && typeof responseData.message === "string" && responseData.message ? responseData.message : null;
	const apiError = responseData && typeof responseData.error === "string" && responseData.error ? responseData.error : null;

	// A server can opt a specific message in as customer-safe; that overrides the status screen
	// so an intentional maintenance/quota notice can still be shown on a 5xx.
	// LSP-21412: the API's mark is `userFacing: true` (contact-api types/userFacingError.ts). The admin
	// api client spreads the JSON body onto the rejection, so it arrives top-level there and under
	// response.data on an axios-shaped error.
	const isMarkedCustomerSafe =
		responseData?.userSafe === true ||
		responseData?.customerSafe === true ||
		responseData?.userFacing === true ||
		error?.userFacing === true;
	const markedTopLevelMessage = typeof error?.message === "string" && error.message.trim() ? error.message : null;
	const markedMessage = isMarkedCustomerSafe ? (apiMessage ?? apiError ?? markedTopLevelMessage) : null;
	if (markedMessage) {
		// The mark lifts the 5xx rule below, not the jargon screen: a message wrapped with userFacing() before
		// it was rewritten must still never reach the customer.
		if (!looksLikeInternalServerText(markedMessage)) return markedMessage;
		console.warn("Suppressed internal-looking error text the API marked userFacing.", {
			status: error?.response?.status ?? error?.httpStatus ?? error?.code,
			serverMessage: markedMessage
		});
		return safeFallback;
	}

	// For 403 Forbidden errors, replace raw technical authorization messages with a friendly fallback.
	// ForbiddenError responses from controllers already carry specific, user-friendly messages
	// (e.g. "You do not have permission to delete this recording.") — those are preserved as-is.
	const errorCode = error.code ?? (typeof error.status === "number" ? error.status : undefined) ?? error?.response?.status;
	if (errorCode === 403) {
		const errorMessage = apiMessage ?? apiError ?? error.message ?? error.error;
		const isTechnicalAuthMessage =
			!errorMessage ||
			typeof errorMessage !== "string" ||
			/\(code \d+\)/.test(errorMessage) ||
			/\(permission \w+\)/.test(errorMessage) ||
			/authorization to access \//.test(errorMessage);
		if (isTechnicalAuthMessage) {
			return "Sorry, you're not allowed to do that. Please contact your administrator.";
		}
	}

	// Anything the server itself failed on is never quoted to the customer. Scoped to a KNOWN 5xx:
	// an error whose status we cannot read still carries deliberate copy on plenty of paths (a
	// rejection built by a caller, a duplicate-contact 4xx surfaced without a status in tests and
	// in some interceptors), so those fall through to the content screen below rather than being
	// blanket-suppressed — suppressing them broke duplicate-contact detection in review.
	const isServerFailure = typeof errorCode === "number" && errorCode >= 500;
	if (isServerFailure) {
		if (apiMessage || apiError || error.message) {
			console.warn("Suppressed server-side error text from the customer-facing message.", {
				status: errorCode,
				serverMessage: apiMessage ?? apiError ?? error.message
			});
		}
		return safeFallback;
	}

	const candidate =
		apiMessage ??
		apiError ??
		(typeof error.message === "string" ? error.message : null) ??
		(typeof error.error === "string" ? error.error : null);

	if (candidate) {
		if (looksLikeInternalServerText(candidate)) {
			console.warn("Suppressed internal-looking error text from the customer-facing message.", {
				status: errorCode,
				serverMessage: candidate
			});
			return safeFallback;
		}
		return candidate;
	}

	// A bare string body is still server text; a body object is definitely not a sentence.
	// Never stringify it into the UI — that was the widest form of the LSP-19484 leak.
	if (responseData) {
		console.warn("Error response carried no usable customer-facing message.", { status: errorCode });
	}
	return safeFallback;
}

/**
 * Turns an arbitrary rejected value into a typed Error so Datadog preserves its
 * message instead of recording a plain object as "[object Object]".
 */
export function getDiagnosticError(error) {
	if (error instanceof Error) return error;
	if (typeof error?.message === "string" && error.message.trim()) return new Error(error.message);
	if (typeof error?.httpStatus === "number") return new Error(`HTTP ${error.httpStatus}`);
	if (typeof error?.code === "number") return new Error(`HTTP ${error.code}`);
	return new Error("Unknown public submission failure");
}

/**
 * Produces searchable, mutually exclusive failure reasons for public-form
 * fallback copy while keeping the raw transport detail out of the UI.
 */
export function getPublicSubmissionFailureDiagnostics(error) {
	const httpStatus = typeof error?.httpStatus === "number" ? error.httpStatus : undefined;
	const code = typeof error?.code === "number" ? error.code : undefined;
	let fallbackReason;

	if (httpStatus === undefined) {
		fallbackReason = "network_error";
	} else if (httpStatus >= 500) {
		fallbackReason = "server_error";
	} else if (httpStatus >= 400 && httpStatus < 500 && error?.isJson !== true) {
		fallbackReason = "non_json_client_error";
	} else if (httpStatus >= 400 && httpStatus < 500 && error?.userFacing !== true) {
		fallbackReason = "untrusted_client_error";
	} else {
		fallbackReason = "json_client_error_without_message";
	}

	return {
		error: getDiagnosticError(error),
		context: { httpStatus, code, fallbackReason }
	};
}

export function isViewOnlyUser(me) {
	return me?.account?.roles?.length === 1 && me.account.roles[0].name === Role.ViewOnly;
}

const PROJECT_ACCESS_DENIED_MESSAGES = [
	"you do not have permission to delete this project.",
	"you do not have permission to update this project.",
	"you do not have permission to share this project.",
	"you do not have permission to comment on this project.",
	"you do not have permission to edit this comment.",
	"you do not have permission to delete this comment.",
	"you do not have permission to delete this document.",
	"you do not have permission to modify this snapshot.",
	"you do not have permission to delete this recording.",
	"you do not have permission to upload content to this project.",
	"you do not have permission to add content to this project."
];

const PROJECT_ACCESS_DENIED_FALLBACK_TARGETS = ["project", "comment", "document", "snapshot", "recording", "access"];

export function isProjectAccessDeniedError(error) {
	if (!error) return false;
	const message = (error?.message ?? "").trim().toLowerCase().replaceAll("don't", "do not").replaceAll("couldn't", "could not");
	const accessDenied = [error?.httpStatus, error?.code, error?.status, error?.response?.status].some((value) => Number(value) === 403);
	const explicitLostProjectAccess = message.includes("no longer have access") && message.includes("project");
	if (!accessDenied && !explicitLostProjectAccess) return false;
	if (message === "sorry, we could not find that project.") return true;
	if (PROJECT_ACCESS_DENIED_MESSAGES.some((msg) => message === msg)) return true;
	if (explicitLostProjectAccess) return true;
	return message.includes("do not have permission") && PROJECT_ACCESS_DENIED_FALLBACK_TARGETS.some((target) => message.includes(target));
}

/**
 * Toggles body scroll behavior to prevent background scrolling when modals/overlays are open.
 * @param {boolean} stopBodyScroll - Whether to stop body scrolling
 *   - true: Disables body scroll (for modal open)
 *   - false: Restores normal body scroll (for modal close)
 */
export function toggleBodyStyles(stopBodyScroll) {
	if (stopBodyScroll) {
		document.body.style.overflow = "hidden";
		document.body.style.position = "fixed";
		document.body.style.width = "100%";
	} else {
		document.body.style.overflow = "";
		document.body.style.position = "";
		document.body.style.width = "";
	}
}

export function getPhoneInputMaxLength(fallback = 15) {
	if (typeof window === "undefined") {
		return fallback;
	}
	const configuredLength = window?.VueTelProps?.inputOptions?.maxlength ?? window?.VueTelProps?.maxlength;
	const parsedLength = Number(configuredLength);
	return Number.isFinite(parsedLength) && parsedLength > 0 ? parsedLength : fallback;
}

export function getTrimmedPhoneFromClipboard(event, maxLength) {
	if (!event) {
		return "";
	}
	const clipboardSource = event.clipboardData ?? (typeof window !== "undefined" ? window.clipboardData : undefined);
	const clipboardText = clipboardSource?.getData?.("text") ?? "";
	const digitsOnly = clipboardText.replace(/\D/g, "");
	const prefix = digitsOnly.length > 0 && clipboardText.trimStart().startsWith("+") ? "+" : "";
	const limit = typeof maxLength === "number" && Number.isFinite(maxLength) && maxLength > 0 ? maxLength : getPhoneInputMaxLength();
	return prefix + digitsOnly.slice(0, limit);
}

/**
 * Filters clipboard text to only allow numeric characters (and decimal point if allowed)
 * @param {ClipboardEvent} event - The paste event
 * @param {boolean} allowDecimals - Whether to allow decimal point (default: false)
 * @returns {string} Filtered numeric string
 */
export function getNumberFromClipboard(event, allowDecimals = false) {
	if (!event) {
		return "";
	}

	const clipboardSource = event.clipboardData ?? (typeof window !== "undefined" ? window.clipboardData : undefined);
	const clipboardText = clipboardSource?.getData?.("text") ?? "";

	// Filter out non-numeric characters
	const numericPattern = allowDecimals ? /[^0-9.]/g : /[^0-9]/g;
	let filteredValue = clipboardText.replace(numericPattern, "");

	// If decimals are allowed, ensure only one decimal point exists
	if (allowDecimals) {
		const parts = filteredValue.split(".");
		if (parts.length > 1) {
			filteredValue = parts[0] + "." + parts[1];
		}
	}
	return filteredValue;
}

export function translateStatus(status, entityType) {
	if (status === null || status === undefined) return "-";
	if (status.toLowerCase() === "inprogress") return "In Progress";
	if (status.toLowerCase() === "completed") return "Completed";
	if (status.toLowerCase() === "notstarted") {
		if (entityType === "conversation") {
			return "-";
		} else {
			return "Not Started";
		}
	}
	if (status.toLowerCase() === "readyforreview") return "Ready for Review";
	if (status.toLowerCase() === "delivered") return "Received";
	return status;
}

export function getStatusLower(status) {
	return status?.toLowerCase() ?? "";
}

export function deepClone(obj) {
	if (typeof structuredClone === "function") {
		try {
			return structuredClone(obj);
		} catch (e) {
			// console.warn("structuredClone failed, falling back to JSON clone", e);
		}
	}
	return JSON.parse(JSON.stringify(obj)); // fallback
}

export function unShiftSnapshots(snapshots, masterdocument) {
	snapshots.unshift({
		...masterdocument,
		id: masterdocument.sourceId,
		conversationId: masterdocument.conversationId,
		src: masterdocument.privateUrl,
		thumbnail: masterdocument.publicThumbnailUrl,
		description: masterdocument.notes ?? "",
		User: masterdocument.createdBy,
		title: timezones.formatDate(masterdocument.createdAt),
		createdAt: masterdocument.createdAt,
		parentId: masterdocument.parentId,
		MasterDocument: masterdocument
	});
}

export function unShiftFiles(files, masterdocument) {
	files.unshift({
		...masterdocument,
		id: masterdocument.sourceId,
		src: masterdocument.privateUrl,
		description: masterdocument.notes ?? "",
		title: masterdocument.title ?? timezones.formatDate(masterdocument.createdAt),
		extension: masterdocument.extension,
		createdAt: masterdocument.createdAt,
		MasterDocument: masterdocument
	});
}

export function unShiftRecordingSessions(recordings, recordingsession) {
	recordings.unshift({
		...recordingsession,
		id: recordingsession.id,
		conversationId: recordingsession.conversationId,
		src: recordingsession.MasterRecording.recordingUrl,
		recordingUrl: recordingsession.MasterRecording.recordingUrl,
		recordingStart: recordingsession.createdAt,
		User: recordingsession.createdBy,
		title: timezones.formatDate(recordingsession.createdAt),
		createdAt: recordingsession.createdAt
	});
}

export function setCurrentSnapshotInLightBox(lightBox, masterdocument) {
	if (lightBox && masterdocument.sourceId) {
		lightBox.setCurrentSnapshotId(masterdocument.sourceId);
	}
}

export function isEmpty(value) {
	return !value || value.trim() === "";
}

/**
 * Finds invalid placeholders in a text string.
 * @param {string} text - The text to check for invalid placeholders.
 * @param {string} placeholderType - The type of placeholder to check for.
 * @returns {boolean} - True if there are invalid placeholders, false otherwise.
 */
export function containsInvalidPlaceholders(text, placeholderType = "scheduleCall") {
	if (!text) return false;

	// Get valid placeholders based on type
	let validPlaceholders;
	switch (placeholderType) {
		case "liveCall":
			validPlaceholders = Object.values(LiveCallPlaceholders);
			break;
		case "recordingRequest":
			validPlaceholders = Object.values(RecordingRequestPlaceholders);
			break;
		case "googleReview":
			validPlaceholders = Object.values(GoogleReviewPlaceholders);
			break;
		default:
			validPlaceholders = Object.values(ScheduleCallPlaceholders);
	}

	// Find all placeholders in text (including empty placeholders ${})
	const matches = text.match(/\$\{([^}]*)\}/g);
	if (!matches) return false;

	// Check if any placeholder is invalid (empty placeholders ${} are considered invalid)
	return matches.some((match) => {
		const isValid = validPlaceholders.includes(match);
		return !isValid;
	});
}

/**
 * Validates template length after placeholder replacement. Counts in Unicode CODE POINTS
 * (LSP-16854), the app-wide unit (LSP-17265, `@common-ui/utilities/characterCount`), so this
 * agrees with the code-point counter/rule the calling settings pages migrate to on the same
 * ticket — an emoji-heavy template must be judged the same way the field's own counter shows it.
 * `template` is trimmed before counting (LSP-16852/16853/16854/16855/16859 review) for the same
 * reason: CharacterLimitedTextArea's own counter trims by default, so measuring the raw value here
 * let whitespace padding near the limit disagree with what the field visibly showed — failing this
 * rule (and blocking save) for a template the counter reported as comfortably under budget. This
 * agreement holds regardless of how the padding got there — typed, inserted via a placeholder chip
 * (a direct model write, bypassing any `v-model.trim`), or already present on a loaded organization.
 * The expansion itself is `expandPlaceholdersForCounting` (helpers/templatePlaceholderCount.ts) —
 * shared with the field's counter since LSP-20508, so the number shown and the number judged are the
 * same measurement of the same string rather than two implementations of one table.
 * @param {string} template - The template text with placeholders
 * @param {Record<string, string>} placeholderValues - Resolved value per placeholder token, as
 * `getTemplatePlaceholderExamples` returns it (the same map the chips and preview display).
 * @param {number} maxLength - Maximum allowed length (default: 500)
 * @returns {true | string} Returns true if valid, or error message string if invalid
 */
export function validateTemplateLength(template, placeholderValues, maxLength = 500, fieldNoun = "template message") {
	if (!template) return true;

	const trimmedTemplate = template.trim();

	// LSP-20508: the per-placeholder length arithmetic that used to live inline here is now
	// `expandPlaceholdersForCounting` (helpers/templatePlaceholderCount.ts), which produces the expanded
	// STRING rather than a sum of deltas. Same numbers, but the field's own counter can now be measured
	// through the identical transform (`CharacterLimitedTextArea`'s `count-as` prop), so the count an
	// admin sees and the verdict that blocks their save can no longer disagree — repeating a `${user}`
	// mention used to push this over the limit while the counter still read "147 / 500".
	const finalLength = countCodePoints(expandPlaceholdersForCounting(trimmedTemplate, placeholderValues || {}));

	if (finalLength > maxLength) {
		return templateOverflowMessage(maxLength, fieldNoun, trimmedTemplate, placeholderValues || {});
	}

	return true;
}

// parser.parse's own regex, copied verbatim from apps/contact-api/src/services/parser.ts. Using the
// server's pattern rather than an allowlist of the three known names matters for MALFORMED input:
// `[^}]+` is greedy, so `${${organization}}` matches as the single unknown key `${organization` and
// the parser leaves it entirely alone, while an allowlist would have substituted the inner token and
// UNDER-counted by 11 — the green-counter-then-400 direction this ticket exists to close.
// break-it round 1
const SERVER_PLACEHOLDER_PATTERN = /(!|\$)\{([^}]+)\}/g;

/**
 * LSP-17482: measures a quick-action notification template the way the SERVER measures it.
 *
 * `ConversationsController.formatNotificationTemplate` (apps/contact-api/src/controllers/conversations.ts)
 * expands `${organization}` / `${user}` / `${teamMember}` FIRST and only then rejects on
 * `unicodeLength()` — which is plain UTF-16 length, since it sums `charAt(i).length`. The client used
 * to count the RAW template, so both mismatch directions were reachable: a short placeholder that
 * expands to a long organization name read green and then 400'd, and a raw-over-limit template whose
 * placeholders SHRINK on expansion was blocked even though the server would have taken it.
 *
 * The placeholder-resolution contract this mirrors (guarded on the server side by
 * apps/contact-api/tests/controllers/notificationTemplateLengthContract.unit.test.ts):
 *   - `${organization}` -> the organization's name
 *   - `${user}` and `${teamMember}` -> the acting user's "firstname lastname", falling back to
 *     lastname alone, then to their email, then to the empty string — see getTeamMemberName() in
 *     ActionSchedule/ActionVideo.
 *   - anything else, including every malformed or nested `${...}` -> left exactly as typed, because
 *     parser.parse only substitutes keys it finds in its params object.
 *
 * KNOWN SERVER-SIDE DIVERGENCE (break-it round 2, deliberately NOT mirrored here): parser.flatten
 * accumulates into a plain `{}`, so the server's `flat[propName]` lookup walks Object.prototype and
 * `${toString}` actually resolves — to "function toString() { [native code] }". The server therefore
 * substitutes 37 characters where this helper counts the 11-character token. That is a bug on the
 * SERVER (it also means those tokens leak JS internals into a customer's SMS); mirroring it would
 * mean hardcoding a prototype allowlist into the client and then having to unpick it the moment
 * parser.flatten switches to Object.create(null) or Object.hasOwn. The intended contract is the one
 * encoded here. Reaching the divergence needs a user typing an Object.prototype method name as a
 * placeholder, so it is reported rather than worked around.
 *
 * Deliberately does NOT trim, unlike validateTemplateLength: this field's counter has counted the raw
 * value since before the shared textarea existed, and that is pinned by its own test. Untrimmed is
 * the conservative side of the boundary anyway — it can only over-count trailing padding the dialog
 * strips on send, never let a too-long message through.
 *
 * @param {string | null | undefined} template - The raw template as typed in the field
 * @param {{ organizationName?: string, userName?: string }} [placeholderValues]
 * @returns {number} UTF-16 length of the string the server will actually validate
 */
export function serverNotificationTemplateLength(template, placeholderValues = {}) {
	const rawTemplate = template ?? "";

	// Mirrors formatNotificationTemplate's own gate, not just its parser: it runs parser.parse only
	// when the raw template contains "${", so a template holding only `!{organization}` reaches the
	// length check UNEXPANDED even though the parser's regex would have matched it.
	if (!rawTemplate.includes("${")) {
		return countUtf16(rawTemplate);
	}

	const expandedTemplate = rawTemplate.replace(SERVER_PLACEHOLDER_PATTERN, (match, _prefix, placeholderName) => {
		if (placeholderName === "organization") {
			return placeholderValues.organizationName ?? "";
		}
		if (placeholderName === "user" || placeholderName === "teamMember") {
			return placeholderValues.userName ?? "";
		}
		// parser.parse leaves a placeholder it cannot resolve exactly as it found it.
		return match;
	});

	return countUtf16(expandedTemplate);
}

/**
 * Counts the number of characters in a string while counting each emoji as a single character.
 * @param {string} value
 * @returns {number} The number of characters in the string.
 */
export function countCharacters(value) {
	if (!value) {
		return 0;
	}

	const segments = [...segmenter.segment(value)];
	return segments.length;
}

/**
 * Counts characters after trimming leading/trailing whitespace.
 * Use this for `:counter-value` on inputs whose validator is `VuetifyValidation.MaxLengthValidation`,
 * or `MinLengthValidation` — those validators trim before measuring, so the
 * counter must trim too or the displayed count will diverge from the validator's verdict on
 * whitespace-padded input (LSP-15742).
 * @param {string} value
 * @returns {number}
 */
export function countCharactersTrimmed(value) {
	return countCharacters(value?.toString().trim());
}

/**
 * Truncates a string to a maximum number of grapheme clusters (visible characters).
 * @param {string} value
 * @param {number} maxLength
 * @returns {string} The truncated string.
 */
export function truncateCharacters(value, maxLength) {
	if (!value) return "";
	const segments = [...segmenter.segment(value)];
	if (segments.length <= maxLength) return value;
	return segments
		.slice(0, maxLength)
		.map((segment) => segment.segment)
		.join("");
}

/**
 * Strips HTML tags from a string and returns plain text.
 *
 * A truthy NON-STRING html is treated as absent rather than coerced (round 2, LSP-16852/16859): this
 * is called directly on API-loaded, unvalidated form fields (e.g. FormPage's formDisplayTitle), and
 * `.replace()` throws TypeError on anything that isn't a real string. Checking the type instead of
 * calling String(html) also means a hostile `toString()`/`valueOf()` is never invoked.
 * @param {string} html - The HTML string to strip
 * @returns {string} The plain text without HTML tags
 */
export function stripHtml(html) {
	if (!html) return "";
	if (typeof html !== "string") return "";
	const stripped = html.replace(/<[^>]*>/g, "").trim();
	const doc = new DOMParser().parseFromString(stripped, "text/html");
	return doc.body.textContent || "";
}

// The save-gate counterpart of stripHtml() above — decode-then-strip, matching contact-api's order —
// lives in its own module (stripHtmlMatchingServerOrder.js), not here, so its "he" dependency doesn't
// ship in every bundle that imports anything else from this file. See that module's doc comment.

/**
 * Parses content wrapped with a single HTML tag.
 * Example: "<p>Title</p>" => { openingTag: "<p>", innerContent: "Title", closingTag: "</p>" }
 *
 * A truthy NON-STRING content (round 4, LSP-16852/16859: an unvalidated API-loaded field, e.g.
 * form.headline persisted as a number/boolean/object) is treated as absent rather than coerced —
 * .match() throws TypeError on anything that isn't a real string, and checking the type instead of
 * calling String(content) means a hostile toString()/valueOf() is never invoked. Same convention as
 * stripHtml() and stripHtmlMatchingServerOrder() above.
 * @param {string} content - HTML-wrapped content
 * @returns {{ openingTag: string, innerContent: string, closingTag: string } | null}
 */
export function parseSingleWrappedHtml(content) {
	if (!content) {
		return null;
	}
	if (typeof content !== "string") {
		return null;
	}

	const wrappedHtmlMatch = content.match(/^(<(\w+)[^>]*>)(.*?)(<\/\2>)$/s);
	if (!wrappedHtmlMatch) {
		return null;
	}

	const [, openingTag, , innerContent, closingTag] = wrappedHtmlMatch;
	return { openingTag, innerContent, closingTag };
}

/**
 * Appends a suffix to content while preserving a single HTML wrapper tag.
 * Example: "<p>Title</p>" + " (Copy)" => "<p>Title (Copy)</p>"
 *
 * A truthy NON-STRING content (round 4, LSP-16852/16859) is treated the same as absent content:
 * FormPage's duplicateForm() calls this with form.value.headline, which contact-api's createForm
 * never validates (LSP-17701/17717), so it can already be a persisted number/boolean/object. This
 * function is the only caller of parseSingleWrappedHtml (see its own guard above for why a typeof
 * check, not String(content), is used), but it is guarded independently here too — same principle
 * as isHtmlOverLimit/stripHtml elsewhere in this PR: every function that might receive unvalidated
 * API data guards itself rather than trusting a caller upstream.
 * @param {string} content - Plain text or HTML-wrapped text content
 * @param {string} suffix - Suffix to append
 * @returns {string} Updated content with suffix appended
 */
export function appendPreservingHtml(content, suffix) {
	if (!content) {
		return suffix || "";
	}
	if (typeof content !== "string") {
		return suffix || "";
	}

	if (!suffix) {
		return content;
	}

	const wrappedHtml = parseSingleWrappedHtml(content);
	if (wrappedHtml) {
		const { openingTag, innerContent, closingTag } = wrappedHtml;
		return `${openingTag}${innerContent}${suffix}${closingTag}`;
	}

	return `${content}${suffix}`;
}
/**
 * Copies text to the clipboard using execCommand with a Clipboard API fallback.
 * execCommand is tried first because it is synchronous and preserves the user-gesture
 * context on iOS Chrome, where the async Clipboard API silently fails after a file
 * download and the subsequent await consumes the gesture token.
 * @param {string} value - The text value to copy to clipboard
 * @returns {Promise<boolean>} Returns true if successful, false if failed
 */
export async function copyToClipboard(value) {
	if (!value) {
		return false;
	}

	// Try the synchronous execCommand approach first so the user-gesture context
	// is still valid on iOS Chrome (awaiting the Clipboard API consumes it).
	const textarea = document.createElement("textarea");
	textarea.value = value;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.left = "-9999px";
	textarea.style.top = "-9999px";
	textarea.style.fontSize = "16px"; // prevents iOS zoom on focus
	document.body.appendChild(textarea);
	try {
		textarea.focus();
		textarea.select();
		textarea.setSelectionRange(0, value.length);
		if (document.execCommand("copy")) {
			return true;
		}
	} catch (execCommandError) {
		console.warn("execCommand copy failed, falling back to Clipboard API:", execCommandError);
	} finally {
		document.body.removeChild(textarea);
	}

	// Fall back to the async Clipboard API for browsers that fully support it.
	if (navigator.clipboard) {
		try {
			await navigator.clipboard.writeText(value);
			return true;
		} catch (clipboardError) {
			console.error("Clipboard copy failed — both execCommand and Clipboard API were unsuccessful:", clipboardError);
		}
	}

	return false;
}

const ALLOWED_CONTROL_KEYS = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Enter"];

/**
 * Filters keydown events for number inputs to prevent non-numeric characters.
 * @param {KeyboardEvent} event - The keyboard event to filter
 */
export function filterNumberKeydown(event) {
	if (event.key === "Unidentified" || event.key === "Process") return;
	if (ALLOWED_CONTROL_KEYS.includes(event.key)) return;
	if (event.ctrlKey || event.metaKey) return;
	if (/^[0-9]$/.test(event.key)) return;
	const target = event.target;
	if (event.key === ".") {
		const targetValue = typeof target?.value === "string" ? target.value : "";
		if (!targetValue.includes(".")) return;

		let selectionStart = targetValue.length;
		let selectionEnd = targetValue.length;
		try {
			if (typeof target.selectionStart === "number") selectionStart = target.selectionStart;
			if (typeof target.selectionEnd === "number") selectionEnd = target.selectionEnd;
		} catch {
			// selectionStart/selectionEnd throws on <input type="number"> in Chrome
		}
		const selectedText = targetValue.slice(selectionStart, selectionEnd);
		if (selectedText.includes(".")) return;
	}

	event.preventDefault();
}
